

import {defineComponent, ref} from "vue";
import {Bar} from 'vue-chartjs'
import {Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartDeferred from 'chartjs-plugin-deferred';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ChartDataLabels, ChartDeferred)

export default defineComponent({
  name: 'Chart',
  components: {
    Bar,
  },
  props: {
    chartItems: {
      type: Array,
    },
    chartColour: {
      type: String,
    },
    chartOrientation: {
      type: String,
    },
    chartLabelPosition: {
      type: String,
    },
    chartType: {
      type: String,
    },
    chartValueType: String,
    chartId: {
      type: String,
    },
    datasetIdKey: {
      type: String,
      default: 'label'
    },
    cssClasses: {
      default: 'bar-chart-wrapper',
      type: String
    },
    styles: {
      type: Object,
      default: () => {
      }
    },
    plugins: {
      type: Object,
      default: () => {
      }
    }
  },
  setup(props) {
    const root = ref<HTMLDivElement>();
    const chartType = props.chartType;
    const chartItems = props.chartItems;
    let chartValue: Array<number> = [];
    let chartLabel: Array<Array<string>> = [];
    let customOrientation;
    const orientation = props.chartOrientation;
    const colour = props.chartColour;
    const labelPosition = props.chartLabelPosition;
    const chartValueType = props.chartValueType;
    let customColour;
    let valuePosition = 'end';
    let valueAnchor = 'end';
    let valueColor = '#444446';
    let showYAxisGrid = false;
    let showXAxisTicks = false;
    let labelFontSize = ref(14);
    let showNameLabel = false;
    let nameLabelPosition = 'end'
    let paddingTop = 0;
    let paddingRight = 0;
    let paddingLeft = 0;
    let chartInPercentage = false;
    let addPercentageSymbol = '';
    let chartHeight = 232;
    let chartWidth = 340;
    let limit = ref(25);
    let maxBarThickness = 32;
    let minBarLength = 5;
    const pageWidth = ref(window.innerWidth);

    if (chartValueType == '1') {
      chartInPercentage = true;
    }

    if (orientation) {
      if (orientation === 'horizontal') {
        customOrientation = 'y';
        showNameLabel = true;
        if (pageWidth.value < 1300) {
          limit.value = 15;
        }
        if (pageWidth.value < 1124) {
          labelFontSize.value = 12;
          limit.value = 15;
        }

        if (labelPosition === 'left') {
          paddingRight = 45;
          paddingLeft = 200;
          nameLabelPosition = 'start';
          limit.value = 18;
          if (pageWidth.value < 1124) {
            paddingLeft = 125;
            limit.value = 15;
          }
        } else {
          valuePosition = 'right';
          valueAnchor = 'start';
          valueColor = '#ffffff';
          paddingRight = 160;
          minBarLength = 38;
          limit.value = 15;
          if (pageWidth.value < 1300) {
            paddingRight = 150;
          }
          if (pageWidth.value < 1124) {
            paddingRight = 140;
            limit.value = 15;
          }
        }
      } else {
        customOrientation = 'x';
        chartHeight = 250;
        showYAxisGrid = true;
        showXAxisTicks = true;
        valuePosition = 'top';
        paddingTop = 35;
        if (pageWidth.value < 1024) {
          maxBarThickness = 29;
        }
      }
    }

    if (colour) {
      if (colour === 'blue') {
        customColour = '#242F62'
      } else {
        customColour = '#E40134'
      }
    }

    if (chartItems) {
      chartItems.forEach((item: any) => {
        let numValue: string;
        if (item.value) {
          numValue = item.value.match(/\d+/)[0];
          chartValue.push(Number(numValue));
        }
        const words = item.label.split(' ');
        let concat: Array<string> = [];
        let result: Array<string> = [];
        let join;

        words.forEach((item: any) => {
          concat.push(item)
          join = concat.join(' ');
          const joinLength = join.match(/[a-zA-Z]/g)?.length;
          if (joinLength > limit.value) {
            result.push(join)
            concat = [];
          }
        })
        join = concat.join(' ');
        result.push(join)
        chartLabel.push(result.filter(Boolean))
      })
    }

    if (chartInPercentage) {
      addPercentageSymbol = '%';
    }

    const chartData = {
      labels: chartLabel,
      datasets: [{data: chartValue, backgroundColor: customColour}]
    }

    const chartOptions = {
      responsive: true,
      indexAxis: customOrientation,
      clamp: true,
      layout: {
        padding: {
          top: paddingTop,
          right: paddingRight,
          left: paddingLeft,
        },
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
        },
        datalabels: {
          labels: {
            value: {
              anchor: valueAnchor,
              align: valuePosition,
              offset: 4,
              formatter: (value) => {
                return Math.round(value) + addPercentageSymbol;
              },
              color: valueColor,
              font: function (context) {
                const width = context?.chart.width;
                let size = Math.round(width / 32);
                size = size > 14 ? 14 : size;
                if (width < 500) {
                  size = 12;
                }
                return {
                  size: size,
                  weight: 'bold'
                };
              },
            },
            name: {
              display: showNameLabel,
              align: nameLabelPosition,
              anchor: nameLabelPosition,
              font: {size: labelFontSize.value},
              clamp: true,
              formatter: function (value, ctx) {
                return ctx.chart.data.labels[ctx.dataIndex];
              }
            },
          },
        },
        deferred: {
          xOffset: 150,
          yOffset: '20%',
          delay: 200
        }
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: showXAxisTicks,
          },
          ticks: {
            color: '#242F62',
            display: showXAxisTicks,

            font: {
              weight: 500,
            }
          },
        },
        y: {
          grid: {
            display: showYAxisGrid,
            drawBorder: false,
          },
          ticks: {
            display: false,
            font: {
              size: labelFontSize.value,
              color: '#444446'
            },
          }
        }
      },
      title: {
        display: false,
      },
      animation: {
        duration: 800,
        easing: 'easeInQuad',
      },
      maxBarThickness: maxBarThickness,
      minBarLength: minBarLength,
      maintainAspectRatio: false,
    };

    return {
      chartData,
      chartOptions,
      chartType,
      chartHeight,
      chartWidth,
      root,
    }
  }
})
